import {ReactComponent as BookmarkIcon} from "../../../../svg/bookmark.svg"
import {useIntl} from "react-intl";
import {useDataContext} from "../../../SurveyContextProvider";
import React, {useMemo, useEffect, useState} from "react";
import styles from './SaveResponse.module.scss';
import {HoverTooltip, Tooltip} from "../../../../UIElements";

export default function SaveResponse({branchData, branchKey}) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const intl = useIntl()
    const [variable, setVariable] = useState("")
    const [saveResponse, setSaveResponse] = useState(false)
    const [editVariable, setEditVariable] = useState(false)
    const [hasDuplicate, setHasDuplicate] = useState(false)
    const initialMessage = branchKey === 'initial'

    const hasChild = (config, parentKey) => {
        if (initialMessage) {
            return Object.values(config).some(branch => branch?.parent === null)
        }
        const parents = new Set(Object.values(config).map(branch => branch?.parent))
        return parents.has(parentKey)
    }

    const foundChild = useMemo(() => {
        return hasChild(surveyData?.config, branchKey)
    }, [surveyData, branchKey])

    const responseDisabled = !foundChild

    const ancestorHasSameVariable = (config, key, newVariable) => {
        const parentKey = config[key]?.parent
        return parentKey ? (config[parentKey]?.response === newVariable || ancestorHasSameVariable(config, parentKey, newVariable)) : false
    }

    const findAllChildren = (config, parentKey) => {
        let children = []
        Object.keys(config).forEach(key => {
            if (config[key].parent === parentKey) {
                children.push(key, ...findAllChildren(config, key))
            }
        })
        return children
    }

    const descendantsHaveSameVariable = (config, key, newVariable) => {
        const childrenKeys = findAllChildren(config, key);
        return childrenKeys.some(childKey => config[childKey]?.response === newVariable)
    };

    const childOrParentHasSameVariable = (newVariable, config, branchKey, checkOnlyDescendants) => {
        const parentHasSameVariable = checkOnlyDescendants ? false : ancestorHasSameVariable(config, branchKey, newVariable)
        const childHasSameVariable = descendantsHaveSameVariable(config, branchKey, newVariable)
        return parentHasSameVariable || childHasSameVariable
    }

    const handleVariableChange = (e) => {
        const newVariable = e.target.value
        setVariable(newVariable)

        // Check for duplicate variable name in tree
        const isDuplicate = childOrParentHasSameVariable(newVariable, surveyData.config, initialMessage ? null : branchKey, initialMessage)
        setHasDuplicate(isDuplicate)
    }

    useEffect(() => {
        const response = branchData?.response || ""
        setVariable(response)
        setSaveResponse(response.length > 0)
    }, [branchData?.response])

    const handleSubmit = e => {
        e.preventDefault()
        const newSurveyData = {...surveyData}
        if (variable?.length < 1) {
            if (initialMessage) {
                delete newSurveyData.config.response
            } else {
                delete newSurveyData.config[branchKey].response
            }
            setSaveResponse(false)
        } else {
            if (initialMessage) {
                newSurveyData.config.response = variable
            } else {
                newSurveyData.config[branchKey].response = variable
            }
        }
        setSurveyData(newSurveyData)
        setEditVariable(false)
    }

    const handleResponseState = state => {
        setSaveResponse(state)
        setEditVariable(state)
        if (!state) {
            const newSurveyData = {...surveyData}
            delete newSurveyData.config[branchKey].response
            setSurveyData(newSurveyData)
        }
    }

    const limitedEditing = surveyData?.status === "FINISHED" || surveyData?.status === "ARCHIVED"

    return (
        <div className={`${styles.container} selection-container`}>
            <BookmarkIcon className={styles.icon}/>
            <div className={`${styles.textContainer} selections fill`}>
                <div>
                    <h5>
                        {(!saveResponse || responseDisabled) ?
                            intl.formatMessage({
                                id: 'saveResponse',
                                defaultMessage: 'Save Response'
                            }) :
                            editVariable ?
                            <form className={`${styles.inputWrap} toolbar-input ${styles.inputWrapOpen}`} onSubmit={handleSubmit}>
                                <input type={"text"} className={`${hasDuplicate ? styles.inputError : ""}`} placeholder={intl.formatMessage({id:'enterVariableName', defaultMessage:'Enter variable name'})} autoFocus value={variable} onChange={handleVariableChange} />
                                <input type={"submit"} disabled={hasDuplicate} value={intl.formatMessage({
                                    id: 'saveVariable',
                                    defaultMessage: 'Save variable',
                                })} />
                                {hasDuplicate && <p className={styles.errorMessage}>{intl.formatMessage({defaultMessage:'This variable name is already used in this branch. Please choose a different name.',id:'variableDuplicateError'})}</p>}
                            </form> :
                                <div className={`${styles.inputWrap} toolbar-input ${styles.inputWrapEdit}`}>
                                    <p>{variable}</p>
                                    <button className={"edit"} onClick={()=>setEditVariable(true)}>
                                        {intl.formatMessage({
                                            defaultMessage:"Edit",
                                            id:"edit",
                                        })}
                                    </button>
                                </div>

                        }
                        {}
                        <Tooltip text={intl.formatMessage({id:'saveResponseTooltip', defaultMessage:'Save the students\' answers to this message for later use. Access saved responses by downloading the .xls file from the \'Results\' tab after the survey is finished.'})} />
                    </h5>
                    {(limitedEditing || responseDisabled) ?
                        <input type={"checkbox"} disabled readOnly={true} className={"switch"} id={`response-${branchKey}`} defaultChecked={saveResponse}/>
                        :
                        <input type={"checkbox"} onChange={()=>handleResponseState(!saveResponse)} className={"switch"} id={`response-${branchKey}`} defaultChecked={saveResponse}/>
                    }
                    {(responseDisabled && !limitedEditing) ?
                        <HoverTooltip text={intl.formatMessage({id:'saveResponseDisabledTooltip', defaultMessage:'Add a subsequent message to enable response saving. Use \'Open Ended Response\' for the final message to thank the student.'})}>
                            <label htmlFor={`response-${branchKey}`} className={`${styles.disabledSwitch} switch-label`} />
                        </HoverTooltip> :
                        <label htmlFor={`response-${branchKey}`} className={`switch-label ${limitedEditing ? styles.disabledSwitch : ""}`} />
                    }
                </div>
            </div>
        </div>
    )
}